import React, { useEffect, useMemo, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { useAppContext } from '../../context/AppContext';
import { getStatsApi, resolveAssetUrl } from '../../services/apiService';
import { KidIcon } from '../kid/KidIcon';
import type { StatsPeriod, StatsResponse } from '../../shared/types/stats';
import { avatarOrDefault } from '../../utils/avatars';
import {
  bucketLabel, encouragementKey, isCurrentOrFuture, memberColor, shiftStatsPeriod, statsRange,
} from '../../utils/statsPeriod';
import { BarList, ColumnChart, CountBarChart } from './StatsCharts';
import { Star } from '../kid/KidIcon';

/**
 * Family stats v1 (docs/design/family-stats.md): Me / Family / Tasks for a week, month, year or
 * all time. Numbers come pre-aggregated from the `stats` Edge Function, which also enforces what
 * children may see of each other (family setting: team / full / admins).
 */

type Tab = 'me' | 'family' | 'tasks';
const PERIODS: StatsPeriod[] = ['week', 'month', 'year', 'all'];

const getLocale = (): string => {
  try {
    return (i18n as any).language || navigator.language || 'en-US';
  } catch {
    return 'en-US';
  }
};

const avatarSrc = (m: { id: string; avatar_url?: string | null }): string => resolveAssetUrl(avatarOrDefault(m.avatar_url));

const StatsPage: React.FC = () => {
  const { current_user, current_family } = useAppContext() as any;
  const [tab, setTab] = useState<Tab>('me');
  const [period, setPeriod] = useState<StatsPeriod>('month');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locale = getLocale();
  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'Europe/Copenhagen'; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = period === 'all'
          ? { period, tz }
          : (() => {
              const { from, to } = statsRange(period, anchor);
              return { period, tz, from: from.toISOString(), to: to.toISOString() };
            })();
        const res = await getStatsApi(params);
        if (!cancelled) setData(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period, anchor, tz, current_family?.id]);

  // Stable colour per member, in the family's member order.
  const familyMembers: any[] = useMemo(() => current_family?.members || [], [current_family?.members]);
  const colorOf = useMemo(() => {
    const map: Record<string, string> = {};
    familyMembers.forEach((m, i) => { map[m.id] = memberColor(i); });
    return (id: string) => map[id] || '#9ca3af';
  }, [familyMembers]);
  const nameOf = (id: string) => familyMembers.find((m) => m.id === id)?.name || '—';

  const periodTitle = (() => {
    if (period === 'all') return i18n.t('stats.all_time');
    const { from, to } = statsRange(period, anchor);
    if (period === 'week') {
      return `${from.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${to.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    if (period === 'month') return anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return String(anchor.getFullYear());
  })();

  const weekLabel = i18n.t('stats.week_short');
  const label = (key: string) => (data ? bucketLabel(key, data.bucket, locale, weekLabel) : key);
  const canGoForward = !isCurrentOrFuture(period, anchor, new Date());

  const pill = (active: boolean) =>
    `min-h-[48px] px-4 md:px-5 rounded-full text-base md:text-lg font-bold border-2 transition-colors ${
      active
        ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
        : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark text-kid-text dark:text-kid-text-dark'
    }`;
  const card = 'bg-kid-card dark:bg-kid-card-dark rounded-kid-card p-5 md:p-6 border-2 border-kid-border dark:border-kid-border-dark text-kid-text dark:text-kid-text-dark';
  // Sentence case, never uppercase (design rule)
  const sectionTitle = 'font-kid-heading font-extrabold text-xl md:text-2xl text-kid-text dark:text-kid-text-dark mb-3';

  const renderMe = (d: StatsResponse) => {
    const enc = i18n.t(encouragementKey(d.me, d.period)).replace('{n}', String(d.me.tasks));
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">
        <div className={card}>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-3xl font-extrabold">✅ {d.me.tasks}</div><div className="text-sm md:text-base font-bold">{i18n.t('stats.tasks_done')}</div></div>
            <div><div className="text-3xl font-extrabold inline-flex items-center gap-1.5"><Star size={30} /> {d.me.points}</div><div className="text-sm md:text-base font-bold">{i18n.t('stats.points')}</div></div>
            <div><div className="text-3xl font-extrabold">🎁 {d.me.rewards}</div><div className="text-sm md:text-base font-bold">{i18n.t('stats.rewards')}</div></div>
          </div>
          <p className="mt-4 text-center font-semibold text-kid-green-shadow dark:text-green-300">{enc}</p>
        </div>
        <div className={card}>
          <h3 className={sectionTitle}>{i18n.t('stats.chart_points')}</h3>
          <ColumnChart
            ariaLabel={i18n.t('stats.chart_points')}
            columns={d.me.series.map((b) => ({ key: b.key, label: label(b.key), segments: [{ value: b.points, color: '#22c55e' }] }))}
          />
        </div>
        {d.me.rooms.length > 0 && (
          <div className={card}>
            <h3 className={sectionTitle}>{i18n.t('stats.where_helped')}</h3>
            <BarList
              ariaLabel={i18n.t('stats.where_helped')}
              rows={d.me.rooms.map((r, i) => ({ key: r.room_id || 'none', label: r.name || i18n.t('stats.no_room'), value: r.count, color: r.room_id ? memberColor(i + 1) : '#9ca3af' }))}
            />
          </div>
        )}
        {d.me.top_tasks.length > 0 && (
          <div className={card}>
            <h3 className={sectionTitle}>{i18n.t('stats.my_top_tasks')}</h3>
            <ul className="space-y-1">
              {d.me.top_tasks.map((t) => (
                <li key={t.title} className="flex justify-between text-sm">
                  <span className="truncate text-kid-text dark:text-kid-text-dark">{t.title}</span>
                  <span className="font-semibold text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('stats.times').replace('{n}', String(t.count))}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderFamily = (d: StatsResponse) => {
    if (!d.can_see_family || !d.family) {
      return <div className={`${card} text-center text-kid-text-muted dark:text-kid-text-muted-dark`}>{i18n.t('stats.family_hidden')}</div>;
    }
    const f = d.family;
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">
        <div className={`${card} text-center lg:col-span-2`}>
          <div className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark">{i18n.t('stats.together')}</div>
          <div className="text-2xl font-extrabold">✅ {f.tasks} · ⭐ {f.points}</div>
        </div>
        <div className={card}>
          <h3 className={sectionTitle}>{i18n.t('stats.who_helped')}</h3>
          {!f.ranked && <p className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark -mt-2 mb-3">{i18n.t('stats.team_view_note')}</p>}
          <BarList
            ariaLabel={i18n.t('stats.who_helped')}
            rows={f.members.map((m, i) => ({
              key: m.id, label: m.name, value: m.points, color: colorOf(m.id),
              avatarSrc: avatarSrc(m), prefix: f.ranked ? `${i + 1}.` : undefined,
            }))}
          />
        </div>
        <div className={card}>
          <h3 className={sectionTitle}>{i18n.t('stats.everyone_over_time')}</h3>
          <ColumnChart
            ariaLabel={i18n.t('stats.everyone_over_time')}
            columns={f.series.map((b) => ({
              key: b.key,
              label: label(b.key),
              segments: f.members.map((m) => ({ value: b.by_member[m.id] || 0, color: colorOf(m.id), title: m.name })),
            }))}
          />
          <div className="flex flex-wrap gap-3 mt-3">
            {f.members.map((m) => (
              <span key={m.id} className="flex items-center gap-1 text-xs text-kid-text-muted dark:text-kid-text-muted-dark">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colorOf(m.id) }} />{m.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTasks = (d: StatsResponse) => {
    if (d.repeating.length === 0) {
      return <div className={`${card} text-center text-kid-text-muted dark:text-kid-text-muted-dark`}>{i18n.t('stats.empty')}</div>;
    }
    // One chart per repeating task: a bar per family member (zeros included so charts compare
    // easily). When the family hides comparisons from children, a child only sees their own bar.
    const chartMembers = d.can_see_family ? familyMembers : familyMembers.filter((m) => m.id === current_user?.id);
    return (
      <div className="space-y-4">
      <div className={card}>
        <h3 className={sectionTitle}>{i18n.t('stats.repeating_title')}</h3>
        <ul className="space-y-4">
          {d.repeating.map((r) => (
            <li key={r.series_id}>
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-semibold text-kid-text dark:text-kid-text-dark truncate">{r.title}</span>
                <span className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark shrink-0">
                  {i18n.t('stats.times').replace('{n}', String(r.count))} · {i18n.t('stats.last_done').replace('{date}', new Date(r.last_at).toLocaleDateString(locale, { day: 'numeric', month: 'short' }))}
                </span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden mt-1.5 bg-kid-chip dark:bg-kid-chip-dark">
                {r.by_member.map((b) => (
                  <div key={b.id} title={`${nameOf(b.id)}: ${b.count}`} style={{ width: `${(b.count / r.count) * 100}%`, background: colorOf(b.id) }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 mt-1">
                {r.by_member.map((b) => (
                  <span key={b.id} className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark">{nameOf(b.id)} {b.count}</span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <h3 className={`${sectionTitle} mt-6`}>{i18n.t('stats.per_task_title')}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {d.repeating.map((r) => {
          const counts = new Map(r.by_member.map((b) => [b.id, b.count]));
          return (
            <div key={r.series_id} className={card}>
              <div className="font-semibold text-kid-text dark:text-kid-text-dark truncate mb-3" title={r.title}>{r.title}</div>
              <CountBarChart
                ariaLabel={`${r.title}: ${i18n.t('stats.times_done_axis')}`}
                bars={chartMembers.map((m) => ({ key: m.id, label: m.name, value: counts.get(m.id) || 0, color: colorOf(m.id) }))}
              />
              <div className="text-[11px] text-kid-text-muted dark:text-kid-text-muted-dark mt-2 text-center">{i18n.t('stats.times_done_axis')}</div>
            </div>
          );
        })}
      </div>
      </div>
    );
  };

  if (!current_user) return null;

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark">
      <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl flex items-center gap-2 mb-4">
        <KidIcon name="stats" size={28} className="shrink-0" />
        {i18n.t('stats.title')}
      </h1>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex gap-1" role="tablist">
          {(['me', 'family', 'tasks'] as Tab[]).map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={pill(tab === t)}>
              {i18n.t(`stats.tab_${t}`)}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button key={p} onClick={() => { setPeriod(p); setAnchor(new Date()); }} className={pill(period === p)}>
              {i18n.t(`stats.period_${p}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        {period !== 'all' ? (
          <button aria-label={i18n.t('stats.prev')} title={i18n.t('stats.prev')} onClick={() => setAnchor((a) => shiftStatsPeriod(a, period, -1))}
            className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold">‹</button>
        ) : <span />}
        <h2 className="font-kid-heading font-extrabold text-xl md:text-2xl text-center">{periodTitle}</h2>
        {period !== 'all' ? (
          <button aria-label={i18n.t('stats.next')} title={i18n.t('stats.next')} disabled={!canGoForward}
            onClick={() => setAnchor((a) => shiftStatsPeriod(a, period, 1))}
            className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold disabled:opacity-30 disabled:pointer-events-none">›</button>
        ) : <span />}
      </div>

      {loading && !data && <div className="text-sm text-gray-500">{i18n.t('common.loading')}</div>}
      {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
      {data && (
        <div className={loading ? 'opacity-60 transition-opacity' : ''}>
          {tab === 'me' && renderMe(data)}
          {tab === 'family' && renderFamily(data)}
          {tab === 'tasks' && renderTasks(data)}
        </div>
      )}
    </div>
  );
};

export default StatsPage;
